import React, { useContext, useEffect, useState } from "react";
import GlobalContext from "../context/GlobalContext";
import EventsService from "../services/events-service";
import { AuthContext } from "react-oauth2-code-pkce";
import dayjs from "dayjs";
import { IconTrash } from "./Icons";

const labelsClasses = [
  "indigo",
  "gray",
  "green",
  "blue",
  "red",
  "purple",
];

export default function EventModal() {
  const {
    setShowEventModal,
    daySelected,
    dispatchCalEvent,
    selectedEvent,
    setSelectedEvent,
    savedEvents,
    monthIndex,
    setMonthIndex,
    loadError, // get error from context
    activeCalendarId, // get active calendar from context
    labels,
    setDaySelected,
    fetchEvents, // Add fetchEvents from context
    isReadonly, // Get readonly status from context
  } = useContext(GlobalContext);
  const { token, tokenData } = useContext(AuthContext);

  const [title, setTitle] = useState(
    selectedEvent ? selectedEvent.title : ""
  );
  const [description, setDescription] = useState(
    selectedEvent ? selectedEvent.description : ""
  );
  const [phoneNumber, setPhoneNumber] = useState(
    selectedEvent ? selectedEvent.phoneNumber : ""
  );
  const [selectedLabelId, setSelectedLabelId] = useState(
    selectedEvent && selectedEvent.label ? selectedEvent.label : null
  );
  // Always treat as not all-day since the checkbox is hidden
  const [allDay] = useState(false);
  const [startTime, setStartTime] = useState(
    selectedEvent && selectedEvent.startTime ? selectedEvent.startTime : "00:00"
  );
  const [endTime, setEndTime] = useState(
    selectedEvent && selectedEvent.endTime ? selectedEvent.endTime : "23:59"
  );
  const [apiError, setApiError] = useState(null);
  const [recurrenceType, setRecurrenceType] = useState("NONE");
  const [recurrenceInterval, setRecurrenceInterval] = useState(1);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [personQuery, setPersonQuery] = useState("");
  const [personPickerOpen, setPersonPickerOpen] = useState(!(selectedEvent && selectedEvent.label));
  const isProjection = selectedEvent ? selectedEvent.projection : false;

  // Recurrence types for the select dropdown
  const recurrenceTypes = [
    { value: 'NONE', label: 'Geen' },
    { value: 'DAILY', label: 'Dagelijks' },
    { value: 'WEEKLY', label: 'Wekelijks' },
    { value: 'MONTHLY', label: 'Maandelijks' },
    { value: 'YEARLY', label: 'Jaarlijks' },
    { value: 'WORKDAY', label: 'Werkdagen' },
  ];

  // Fade out error after 3 seconds
  useEffect(() => {
    if (apiError) {
      const timer = setTimeout(() => setApiError(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [apiError]);

  // Load event data into form when selectedEvent changes
  useEffect(() => {
    if (selectedEvent) {
      setTitle(selectedEvent.title || "");
      setDescription(selectedEvent.description || "");
      setPhoneNumber(selectedEvent.phoneNumber || "");
      setSelectedLabelId(selectedEvent.label || null);
      setStartTime(selectedEvent.startTime || "00:00");
      setEndTime(selectedEvent.endTime || "23:59");
      setRecurrenceType(selectedEvent.recurrenceType || "NONE");
      setRecurrenceInterval(selectedEvent.recurrenceInterval || 1);
      setRecurrenceEndDate(selectedEvent.recurrenceEndDate || "");
      setPersonQuery("");
      setPersonPickerOpen(!selectedEvent.label);
      // Set the correct day for the event modal
      if (selectedEvent.day) {
        setDaySelected && setDaySelected(window.dayjs ? window.dayjs(selectedEvent.day) : new Date(selectedEvent.day));
      }
    }
  }, [selectedEvent]);

  function selectPerson(lbl) {
    if (isProjection || isReadonly) return;
    setSelectedLabelId(lbl.id);
    setTitle(lbl.name || "");
    setPhoneNumber(lbl.phoneNumber || "");
    setPersonQuery("");
    setPersonPickerOpen(false);
  }

  const selectedPerson = Array.isArray(labels)
    ? labels.find((lbl) => lbl.id === selectedLabelId)
    : null;
  const personQueryTrimmed = personQuery.trim().toLowerCase();
  const personMatches = !personQueryTrimmed || !Array.isArray(labels)
    ? []
    : labels.filter((lbl) => {
        const name = (lbl.name || "").toLowerCase();
        const phone = (lbl.phoneNumber || "").toLowerCase();
        return name.includes(personQueryTrimmed) || phone.includes(personQueryTrimmed);
      });

  // Defensive: always ensure daySelected is a dayjs object before using .format
  const safeDaySelected = dayjs.isDayjs(daySelected) ? daySelected : dayjs(daySelected);

  // Helper: check if two events overlap (assumes same day)
  function isOverlap(eventA, eventB) {
    if (!eventA.startTime || !eventA.endTime || !eventB.startTime || !eventB.endTime) return false;
    const [aStartH, aStartM] = eventA.startTime.split(":").map(Number);
    const [aEndH, aEndM] = eventA.endTime.split(":").map(Number);
    const [bStartH, bStartM] = eventB.startTime.split(":").map(Number);
    const [bEndH, bEndM] = eventB.endTime.split(":").map(Number);
    const aStart = aStartH * 60 + aStartM;
    const aEnd = aEndH * 60 + aEndM;
    const bStart = bStartH * 60 + bStartM;
    const bEnd = bEndH * 60 + bEndM;
    return aStart < bEnd && bStart < aEnd;
  }

  // Handle the confirmation for deleting/skipping an event
  const handleDeleteConfirm = async () => {
    if (!selectedEvent) return;
    
    try {
      setApiError(null);
      
      if (selectedEvent.projection) {
        // For projected events, skip the occurrence
        await EventsService.skipProjectedEvent(selectedEvent, token);
        // Refetch events to update the display
        if (typeof fetchEvents === 'function') {
          setTimeout(() => fetchEvents(), 0);
        }
      } else {
        // For regular events, delete normally
        dispatchCalEvent({
          type: "delete",
          payload: selectedEvent,
        });
        await EventsService.deleteEvent(selectedEvent.id, token);
      }
      
      setShowDeleteDialog(false);
      setShowEventModal(false);
      
    } catch (err) {
      const action = selectedEvent.projection ? "overslaan" : "verwijderen";
      setApiError(`Fout bij ${action} van event: ` + (err.message || err));
      setShowDeleteDialog(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false);
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setApiError(null);
    // Convert daySelected to UTC midnight epoch
    const utcDay = Date.UTC(
      safeDaySelected.year(),
      safeDaySelected.month(),
      safeDaySelected.date(),
      0, 0, 0, 0
    );
    const calendarEvent = {
      title,
      description,
      label: selectedLabelId, // pass label id (can be null)
      day: utcDay, // store as UTC epoch
      id: selectedEvent ? selectedEvent.id : Date.now(),
      allDay,
      startTime: allDay ? null : startTime,
      endTime: allDay ? null : endTime,
      phoneNumber,
      recurrenceType: recurrenceType !== 'NONE' ? recurrenceType : null,
      recurrenceInterval: recurrenceType !== 'NONE' ? recurrenceInterval : null,
      recurrenceEndDate: recurrenceType !== 'NONE' ? recurrenceEndDate : null,
      calendarId: activeCalendarId, // pass calendarId
    };
    if (!selectedLabelId) {
      setApiError("Kies een persoon of shift.");
      return;
    }
    if (!phoneNumber || !String(phoneNumber).trim()) {
      setApiError("Deze persoon of shift heeft geen doorschakelnummer.");
      return;
    }
    // Overlap check
    if (!allDay && startTime && endTime) {
      const eventsOnDay = savedEvents.filter(
        evt => evt.day === utcDay && evt.id !== calendarEvent.id
      );
      const overlaps = eventsOnDay.some(evt => isOverlap(evt, calendarEvent));
      if (overlaps) {
        setApiError("Tijd overlapt met een andere afspraak!");
        return;
      }
    }
    try {
      let result;
      if (selectedEvent) {
        result = await EventsService.updateEvent(calendarEvent, token);
        dispatchCalEvent({ type: "update", payload: result });
      } else {
        result = await EventsService.saveEvent(calendarEvent, token);
        dispatchCalEvent({ type: "push", payload: result });
      }
      // Always refetch if the event was or is recurring (i.e. if either the old or new event has recurrenceType)
      const wasRecurring = selectedEvent && selectedEvent.recurrenceType && selectedEvent.recurrenceType !== 'NONE';
      const isNowRecurring = calendarEvent.recurrenceType && calendarEvent.recurrenceType !== 'NONE';
      if (wasRecurring || isNowRecurring) {
        // Use the shared fetchEvents() from context so year is determined consistently
        if (typeof fetchEvents === 'function') {
          await fetchEvents();
        } else {
          const year = (typeof window !== 'undefined' && window.currentDate && window.currentDate.year) ? window.currentDate.year() : dayjs().year();
          const events = await EventsService.fetchEvents(monthIndex, year, token, activeCalendarId);
          dispatchCalEvent({ type: 'set', payload: events });
        }
      }
      setShowEventModal(false);
    } catch (err) {
      setApiError("Fout bij opslaan van event: " + (err.message || err));
    }
  }
  return (
    <div className="h-screen w-full fixed left-0 top-0 flex justify-center items-center z-[60] bg-black/30">
      <form className="bg-white dark:bg-gray-900 dark:text-gray-100 rounded-none md:rounded-xl md:shadow-xl w-full h-full md:w-1/2 lg:w-1/3 md:max-h-[90vh] md:h-auto overflow-y-auto">
        {loadError && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-3 mb-4 rounded">
            {loadError}
          </div>
        )}
        <header className="bg-white dark:bg-gray-900 px-4 py-3 flex justify-between items-center sticky top-0 z-10 border-b border-gray-100 dark:border-gray-800">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
            {selectedEvent ? 'Bewerk afspraak' : 'Nieuwe afspraak'}
          </h2>
          <div className="flex items-center gap-1">
            {selectedEvent && !isReadonly && (
              <button
                type="button"
                onClick={() => setShowDeleteDialog(true)}
                className="btn-icon text-red-600 hover:bg-red-50"
                title="Verwijderen"
                aria-label="Verwijderen"
              >
                <IconTrash size={18} />
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowEventModal(false)}
              className="btn-icon"
              aria-label="Sluiten"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
          </div>
        </header>
        {isProjection && selectedEvent && (() => {
          // Find the original event
          const allEventsWithSameId = savedEvents.filter(evt => evt.id === selectedEvent.id && !evt.projection);
          const originalEvent = allEventsWithSameId.length > 0 ? allEventsWithSameId[0] : null;
          const originalDate = originalEvent ? dayjs(originalEvent.day) : null;
          
          return (
            <div className="bg-yellow-50 border-l-4 border-yellow-500 text-yellow-900 p-4 mx-3 mt-3 mb-2 rounded">
              <div className="flex items-start gap-3">
                <span className="material-icons-outlined text-yellow-600 flex-shrink-0">
                  info
                </span>
                <div className="flex-1">
                  <p className="font-semibold mb-2">
                    Dit is een terugkerende afspraak
                  </p>
                  <p className="text-sm mb-2">
                    Deze afspraak is onderdeel van een terugkerende serie en kan hier niet worden bewerkt.
                  </p>
                  {originalDate && (
                    <p className="text-sm mb-3">
                      <strong>Originele afspraak:</strong> {originalDate.locale('nl').format('dddd D MMMM YYYY')}
                    </p>
                  )}
                  {originalEvent && (
                    <button
                      type="button"
                      onClick={() => {
                        const originalDateObj = dayjs(originalEvent.day);
                        // Update month view to show the original event's month
                        if (typeof setMonthIndex === 'function') {
                          setMonthIndex(originalDateObj.month());
                        }
                        // Update the window.currentDate for navigation
                        if (typeof window !== 'undefined') {
                          window.currentDate = originalDateObj;
                        }
                        // Set the selected event and day FIRST
                        setSelectedEvent(originalEvent);
                        setDaySelected(originalDateObj);
                      }}
                      className="text-sm bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded transition-colors mb-3"
                    >
                      Ga naar originele afspraak
                    </button>
                  )}
                  <div className="text-sm text-yellow-800 bg-yellow-100 p-2 rounded">
                    <strong>Tip:</strong> Gebruik de verwijderknop (🗑️) rechtsboven om alleen deze specifieke instantie te verwijderen zonder de hele serie te beïnvloeden.
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
        <div className="p-4 md:p-3">
          <div className="grid grid-cols-1 md:grid-cols-1/5 items-start md:items-end gap-y-4 md:gap-y-7">
            <div></div>
            <input
              type="text"
              name="title"
              placeholder="Titel (kies een persoon of shift)"
              value={title}
              required
              className="input-field h-10 text-lg font-semibold"
              onChange={(e) => setTitle(e.target.value)}
              disabled={isProjection || isReadonly}
            />
            <span className="material-icons-outlined text-gray-400">
              schedule
            </span>
            <p className="text-gray-600">{safeDaySelected.format("dddd, MMMM DD")}</p>
            { /* 
            <span className="material-icons-outlined text-gray-400">
              event
            </span>
            
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={allDay}
                onChange={() => !isProjection && !isReadonly && setAllDay((prev) => !prev)}
                className="form-checkbox"
                disabled={isProjection || isReadonly}
              />
              Duurt hele dag
            </label>
            */ }
            {!allDay && (
              <>
                <span className="material-icons-outlined text-gray-400">
                  schedule
                </span>
                <input
                  type="time"
                  name="startTime"
                  value={startTime}
                  onChange={(e) => !isProjection && !isReadonly && setStartTime(e.target.value)}
                  className="input-field"
                  required
                  disabled={isProjection || isReadonly}
                />
                <span className="material-icons-outlined text-gray-400">
                  schedule
                </span>
                <input
                  type="time"
                  name="endTime"
                  value={endTime}
                  onChange={(e) => !isProjection && !isReadonly && setEndTime(e.target.value)}
                  className="input-field"
                  required
                  disabled={isProjection || isReadonly}
                />
              </>
            )}
            <span className="material-icons-outlined text-gray-400">
              segment
            </span>
            <input
              type="text"
              name="description"
              placeholder="Voer een beschrijving in"
              value={description}
              className="input-field"
              onChange={(e) => setDescription(e.target.value)}
              disabled={isReadonly}
            />
            <span className="material-icons-outlined text-gray-400">
              phone
            </span>
            <input
              type="text"
              name="phoneNumber"
              placeholder="Nummer volgt de gekozen persoon of shift"
              value={phoneNumber}
              readOnly
              className="input-field bg-gray-50"
            />
            <span className="material-icons-outlined text-gray-400">
              bookmark_border
            </span>
            <div className="flex flex-col gap-1 min-w-0">
              {(!labels || labels.length === 0) ? (
                <p className="text-xs text-gray-400">Voeg eerst een persoon of shift toe in de zijbalk.</p>
              ) : (
                <>
                  {selectedPerson && (
                    <button
                      type="button"
                      onClick={() => {
                        if (isProjection || isReadonly) return;
                        setPersonPickerOpen((open) => !open);
                        setPersonQuery("");
                      }}
                      className={`flex items-center gap-2 text-left px-2 py-1 rounded border border-blue-600 bg-blue-50 ${(isProjection || isReadonly) ? 'opacity-50 pointer-events-none' : ''}`}
                    >
                      <span
                        className="w-4 h-4 rounded-full border flex-shrink-0"
                        style={{ background: selectedPerson.color || '#ccc' }}
                      />
                      <span className="text-sm text-gray-700 truncate">{selectedPerson.name}</span>
                      {selectedPerson.phoneNumber && (
                        <span className="text-xs text-gray-400 truncate ml-auto">{selectedPerson.phoneNumber}</span>
                      )}
                    </button>
                  )}
                  {(!selectedPerson || personPickerOpen) && !isProjection && !isReadonly && (
                    <>
                      <input
                        type="text"
                        value={personQuery}
                        onChange={(e) => setPersonQuery(e.target.value)}
                        placeholder="Zoek persoon of shift"
                        autoFocus
                        className="input-field"
                      />
                      {personQueryTrimmed ? (
                        personMatches.length > 0 ? (
                          <div className="flex flex-col gap-1 overflow-y-auto" style={{ maxHeight: "12rem" }}>
                            {personMatches.map((lbl) => (
                              <button
                                type="button"
                                key={lbl.id}
                                onClick={() => selectPerson(lbl)}
                                className={`flex items-center gap-2 text-left px-2 py-1 rounded border ${selectedLabelId === lbl.id ? 'border-blue-600 bg-blue-50' : 'border-transparent hover:bg-gray-50'}`}
                              >
                                <span
                                  className="w-4 h-4 rounded-full border flex-shrink-0"
                                  style={{ background: lbl.color || '#ccc' }}
                                />
                                <span className="text-sm text-gray-700 truncate">{lbl.name}</span>
                                {lbl.phoneNumber && (
                                  <span className="text-xs text-gray-400 truncate ml-auto">{lbl.phoneNumber}</span>
                                )}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400">Geen personen of shifts gevonden.</p>
                        )
                      ) : (
                        <p className="text-xs text-gray-400">Typ een naam of nummer om te zoeken.</p>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
            <span className="material-icons-outlined text-gray-400">repeat</span>
            <div className="flex gap-x-2">
              <label className="text-xs text-gray-600 block mb-1">Herhaling:</label>
              <select
                className="input-field"
                value={recurrenceType}
                onChange={e => setRecurrenceType(e.target.value)}
                disabled={isProjection || isReadonly}
              >
                {recurrenceTypes.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            {recurrenceType !== 'NONE' && (
              <>
                <span className="material-icons-outlined text-gray-400">repeat_one</span>
                <div className="flex gap-x-2">
                  <label className="text-xs text-gray-600 block mb-1">elke </label>
                  <input
                    type="number"
                    min={1}
                    className="input-field"
                    value={recurrenceInterval}
                    onChange={e => setRecurrenceInterval(Number(e.target.value))}
                    disabled={isProjection || isReadonly}
                  />
                  <span className="text-xs text-gray-600">
                    {recurrenceType === 'DAILY' ? 'dagen' : recurrenceType === 'WEEKLY' ? 'weken' : recurrenceType === 'MONTHLY' ? 'maanden' : recurrenceType === 'WORKDAY' ? 'werkdagen' : 'jaren'}
                  </span>
                </div>
                <span className="material-icons-outlined text-gray-400">event</span>
                <div className="flex-1">
                  <label className="text-xs text-gray-600 block mb-1">Einddatum:</label>
                  <input
                    type="date"
                    className="input-field"
                    value={recurrenceEndDate}
                    onChange={e => setRecurrenceEndDate(e.target.value)}
                    disabled={isProjection || isReadonly}
                  />
                </div>
              </>
            )}
          </div>
        </div>
        <footer className="flex justify-end border-t border-gray-100 dark:border-gray-800 p-4 md:p-3 sticky bottom-0 bg-white dark:bg-gray-900">
          {apiError && (
            <div className="text-red-500 text-sm mr-4 self-center">{apiError}</div>
          )}
          {!isProjection && !isReadonly && (
            <button
              type="submit"
              onClick={handleSubmit}
              className="btn-primary w-full md:w-auto"
            >
              Opslaan
            </button>
          )}
        </footer>
      </form>
      
      {/* Confirmation dialog for deleting/skipping events */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black/30 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-xl p-6 m-4 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {selectedEvent && selectedEvent.projection ? 'Terugkerende afspraak overslaan' : 'Afspraak verwijderen'}
            </h3>
            <p className="text-gray-700 mb-6">
              {selectedEvent && selectedEvent.projection 
                ? 'Weet je zeker dat je deze terugkerende afspraak wilt overslaan? Deze wordt toegevoegd aan de overgeslagen gebeurtenissen van de terugkerende serie.'
                : 'Weet je zeker dat je deze afspraak permanent wilt verwijderen?'
              }
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={handleDeleteCancel}
                className="btn-ghost"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700"
              >
                {selectedEvent && selectedEvent.projection ? 'Overslaan' : 'Verwijderen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
